// One pass over the built feed, producing every positional decision the thread
// renderer needs: what folds away, what unrolls where, whose footer is
// redundant, and which item is last. Pure (types only) so it lives here in
// shared/client and stays unit-testable.
//
// These used to be separate traversals in the component, and they disagreed.
// The footer pass ran over the pre-fold feed, so it hid a footer because of a
// breadcrumb that had already moved into a later answer — and since the footer
// hosted relocated content, a pending card could end up rendered nowhere. They
// are one function now because they are one question: what does the feed
// actually show?
import {
  type AnchoredBiscuits,
  isResponseMessage,
  NO_ANCHORED_BISCUITS,
  selectAnchoredBiscuits,
} from "./anchoredBiscuits.ts";
import { messageIsBreadcrumb } from "./breadcrumbs.ts";
import type { RootItem } from "./eventTree.ts";
import { type FeedReactions, selectReactions } from "./reactions.ts";
import { NO_TURN_BREADCRUMBS, selectTurnBreadcrumbs } from "./turnBreadcrumbs.ts";

export type FeedLayout = AnchoredBiscuits &
  Pick<FeedReactions, "reactionsByMessageId"> & {
  /**
   * Root-item indices the feed must not render at top level: runs folded into
   * an answer, and reaction rows drawn on the message they belong to.
   */
  foldedIndices: ReadonlySet<number>;
  /** Folded indices keyed by the answer whose eyebrow unrolls them. */
  byResponseMessageId: ReadonlyMap<number, number[]>;
  /** The folded run that has no answer yet — the thinking indicator's to show. */
  runningIndices: readonly number[];
  /** Root-item index → whether its footer would only add noise. */
  hideFooterByIndex: ReadonlyMap<number, boolean>;
  /** The last index the feed actually renders, or -1 when it renders nothing. */
  lastVisibleIndex: number;
};

/**
 * Whether a footer would only add noise, in a single reverse scan.
 *
 * A footer is redundant when the thing below it already reads as a
 * continuation: an answer followed by a breadcrumb, or a breadcrumb followed by
 * another breadcrumb or an event group. The last breadcrumb in a run keeps its
 * footer, because what follows it is ordinary content.
 *
 * "Followed by" means *rendered* below, which is why folded indices are skipped
 * rather than merely ignored: they don't appear in the feed, so they can't be
 * what follows anything.
 */
function computeFooterVisibility(
  rootItems: RootItem[],
  foldedIndices: ReadonlySet<number>,
): { hideFooterByIndex: Map<number, boolean>; lastVisibleIndex: number } {
  const hideFooterByIndex = new Map<number, boolean>();
  let lastVisibleIndex = -1;
  let nextVisibleIsBreadcrumb = false;
  let nextVisibleIsBreadcrumbOrGroup = false;

  for (let i = rootItems.length - 1; i >= 0; i--) {
    const item = rootItems[i];
    if (foldedIndices.has(i)) continue;

    if (item.kind === "message") {
      if (lastVisibleIndex === -1) lastVisibleIndex = i;

      const artifacts = item.event.message.artifacts ?? [];
      const isAnswer = isResponseMessage(artifacts);
      const isBreadcrumb = messageIsBreadcrumb(item.event.message);

      if (isAnswer) {
        hideFooterByIndex.set(i, nextVisibleIsBreadcrumb);
        nextVisibleIsBreadcrumb = false;
        nextVisibleIsBreadcrumbOrGroup = false;
      } else if (isBreadcrumb) {
        hideFooterByIndex.set(i, nextVisibleIsBreadcrumbOrGroup);
        nextVisibleIsBreadcrumb = true;
        nextVisibleIsBreadcrumbOrGroup = true;
      } else {
        nextVisibleIsBreadcrumb = false;
        nextVisibleIsBreadcrumbOrGroup = false;
      }
    } else {
      if (lastVisibleIndex === -1) lastVisibleIndex = i;
      // An event group reads like a breadcrumb to whatever sits above it.
      nextVisibleIsBreadcrumb = false;
      nextVisibleIsBreadcrumbOrGroup = true;
    }
  }

  return { hideFooterByIndex, lastVisibleIndex };
}

/**
 * Everything the renderer needs to place the feed.
 *
 * One entry point, one input, one walk of the feed per question it answers —
 * what folds, whose footer is redundant, what is last, and which cards move
 * where. They all key off the same predicates, so they cannot disagree about
 * which messages exist or which of them is an answer.
 *
 * `fold` is the feature gate, `isTurnRunning` says whether an answer is still
 * coming, and `isEventView` whether the reader asked to see the raw trace; all
 * three are arguments rather than read here so the whole layout stays a pure
 * function of its inputs. Holding content back is only ever justified while
 * a turn is working — on an idle thread it would hide it for good. Gated off, nothing folds and
 * footer visibility is computed over the plain feed — which is exactly the
 * pre-feature behaviour, by construction rather than by a parallel code path.
 *
 * Folding is also off in the event view, whatever the gate says (BIG-7475). It
 * buys a tidy feed by withholding the run until an answer closes it, and the
 * event view exists precisely to watch events land as they happen — the two
 * cannot both be served. Nothing else about that view changes: `buildRootItems`
 * still groups the feed by turn, so the run keeps its shape and simply stays in
 * the feed rather than folding into the answer.
 */
export function selectFeedLayout(
  rootItems: RootItem[],
  {
    fold,
    isTurnRunning,
    isEventView,
  }: { fold: boolean; isTurnRunning: boolean; isEventView: boolean },
): FeedLayout {
  const shouldFold = fold && !isEventView;
  const turns = shouldFold
    ? selectTurnBreadcrumbs(rootItems, { isTurnRunning })
    : NO_TURN_BREADCRUMBS;
  const { byResponseMessageId, runningIndices } = turns;
  // Reactions hide regardless of the fold gate: a row drawn as a badge on the
  // user's message must never also render as a reply below it.
  const { reactionsByMessageId, reactionIndices } = selectReactions(rootItems);
  const foldedIndices =
    reactionIndices.size === 0
      ? turns.foldedIndices
      : new Set([...turns.foldedIndices, ...reactionIndices]);
  const { hideFooterByIndex, lastVisibleIndex } = computeFooterVisibility(rootItems, foldedIndices);
  const { byAnchorMessageId, relocatedIds } = shouldFold
    ? selectAnchoredBiscuits(rootItems, { isTurnRunning })
    : NO_ANCHORED_BISCUITS;
  return {
    byResponseMessageId,
    runningIndices,
    foldedIndices,
    hideFooterByIndex,
    lastVisibleIndex,
    byAnchorMessageId,
    relocatedIds,
    reactionsByMessageId,
  };
}
